import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, setDoc, getDoc, collection, getDocs } from 'firebase/firestore';
import { auth, db } from './firebase';

// Generate random color for new users
const getRandomColor = () => {
  const colors = ['#FF69B4', '#FFD700', '#00CED1', '#FF6347', '#9370DB', '#32CD32', '#FF8C00', '#1E90FF'];
  return colors[Math.floor(Math.random() * colors.length)];
};

// Sign up new user
export const signUp = async (email, password, name) => {
  try {
    // Create auth user
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Create user document in Firestore
    const color = getRandomColor();
    await setDoc(doc(db, 'users', user.uid), {
      name: name,
      email: email,
      color: color,
      createdAt: new Date()
    });

    return {
      uid: user.uid,
      email: user.email,
      name: name,
      color: color
    };
  } catch (error) {
    console.error('Signup error:', error);
    throw new Error(error.message);
  }
};

// Login user
export const login = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Get user data from Firestore
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return {
        uid: user.uid,
        email: user.email,
        name: userData.name,
        color: userData.color
      };
    } else {
      throw new Error('User data not found');
    }
  } catch (error) {
    console.error('Login error:', error);
    throw new Error(error.message);
  }
};

// Logout user
export const logout = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Logout error:', error);
    throw new Error(error.message);
  }
};

// Get current user
export const getCurrentUser = () => {
  return auth.currentUser;
};

// Get user data from Firestore
export const getUserData = async (uid) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (userDoc.exists()) {
      return {
        uid: uid,
        ...userDoc.data()
      };
    }
    return null;
  } catch (error) {
    console.error('Get user data error:', error);
    return null;
  }
};

// Get all users (for inviting)
export const getAllUsers = async () => {
  try {
    const usersSnapshot = await getDocs(collection(db, 'users'));
    const users = [];
    usersSnapshot.forEach((doc) => {
      users.push({
        uid: doc.id,
        ...doc.data()
      });
    });
    return users;
  } catch (error) {
    console.error('Get all users error:', error);
    return [];
  }
};

// Listen to auth state changes
export const onAuthChange = (callback) => {
  return onAuthStateChanged(auth, async (user) => {
    if (user) {
      const userData = await getUserData(user.uid);
      callback(userData);
    } else {
      callback(null);
    }
  });
};

export const firebaseAuth = {
  signUp,
  login,
  logout,
  getCurrentUser,
  getUserData,
  getAllUsers,
  onAuthChange
};
